import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, ShieldAlert, Users as UsersIcon, Search } from "lucide-react";
import { toast } from "sonner";
import Pagination, { paginate } from "@/components/Pagination";

const ROLES = [
  { v: "admin", label: "Administrateur" },
  { v: "agent", label: "Agent" },
];

const ROLE_TONE = {
  dev: "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100",
  admin: "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100",
  agent: "bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100",
};

const emptyForm = { email: "", password: "", name: "", role: "agent", society_id: "" };

export default function DevUsers() {
  const [users, setUsers] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [search, setSearch] = useState("");
  const [societyFilter, setSocietyFilter] = useState("__all__");
  const [roleFilter, setRoleFilter] = useState("__all__");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    try {
      const [u, s] = await Promise.all([api.get("/dev/users"), api.get("/societies")]);
      setUsers(u.data || []);
      setSocieties(s.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (societyFilter !== "__all__") {
        const sid = societyFilter === "__none__" ? null : societyFilter;
        if ((u.society_id || null) !== sid) return false;
      }
      if (roleFilter !== "__all__" && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        u.email?.toLowerCase().includes(q) ||
        u.name?.toLowerCase().includes(q) ||
        u.society_nom?.toLowerCase().includes(q)
      );
    });
  }, [users, search, societyFilter, roleFilter]);

  const pag = paginate(filtered, page);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u.id);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role,
      society_id: u.society_id || "",
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = {
          email: form.email,
          name: form.name,
          role: form.role,
          society_id: form.society_id || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing}`, payload);
        toast.success("Utilisateur modifié");
      } else {
        await api.post("/users", {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
          society_id: form.society_id || null,
        });
        toast.success("Utilisateur créé");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/users/${confirmId}`);
      toast.success("Utilisateur supprimé");
      setConfirmId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Suppression échouée");
    }
  };

  const stats = useMemo(() => ({
    total: users.length,
    dev: users.filter((u) => u.role === "dev").length,
    admin: users.filter((u) => u.role === "admin").length,
    agent: users.filter((u) => u.role === "agent").length,
    unbound: users.filter((u) => !u.society_id).length,
  }), [users]);

  return (
    <div data-testid="page-dev-users">
      <PageHeader
        title="Utilisateurs — vue globale DEV"
        subtitle="Création, modification et suppression de tous les comptes utilisateurs, toutes sociétés confondues."
        testid="page-dev-users-header"
        accent="amber"
        actions={
          <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-dev-user">
            <Plus className="w-4 h-4 mr-2" /> Nouvel utilisateur
          </Button>
        }
      />

      <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <strong>Espace réservé au rôle DEV.</strong> Vous pouvez créer/modifier/supprimer les administrateurs et agents de toutes les sociétés. <br className="hidden sm:inline" />
          <strong>Le compte DEV lui-même est verrouillé</strong> et se configure uniquement via les variables d'environnement <code className="px-1 py-0.5 rounded bg-amber-100 border border-amber-200 text-[11px]">DEV_EMAIL</code> / <code className="px-1 py-0.5 rounded bg-amber-100 border border-amber-200 text-[11px]">DEV_PASSWORD</code> / <code className="px-1 py-0.5 rounded bg-amber-100 border border-amber-200 text-[11px]">DEV_NAME</code> du fichier <code className="px-1 py-0.5 rounded bg-amber-100 border border-amber-200 text-[11px]">.env</code>.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: "Total", value: stats.total, tone: "text-slate-700" },
          { label: "Devs", value: stats.dev, tone: "text-amber-700" },
          { label: "Admins", value: stats.admin, tone: "text-emerald-700" },
          { label: "Agents", value: stats.agent, tone: "text-slate-700" },
          { label: "Sans société", value: stats.unbound, tone: "text-rose-700" },
        ].map((k) => (
          <Card key={k.label} className="p-4 rounded-xl border border-slate-200 bg-white">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{k.label}</div>
            <div className={`font-heading text-2xl font-bold ${k.tone}`}>{k.value}</div>
          </Card>
        ))}
      </div>

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-slate-500" />
            <div className="font-heading font-semibold text-slate-900">Tous les utilisateurs</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-8 h-9 w-full sm:w-56"
                data-testid="input-dev-user-search"
              />
            </div>
            <Select value={societyFilter} onValueChange={(v) => { setSocietyFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-56 h-9" data-testid="select-filter-society"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes les sociétés</SelectItem>
                <SelectItem value="__none__">— Sans société —</SelectItem>
                {societies.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40 h-9" data-testid="select-filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous rôles</SelectItem>
                {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Rôle</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Société</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pag.rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-12">Aucun utilisateur.</TableCell></TableRow>
            )}
            {pag.rows.map((u) => {
              const isDev = u.role === "dev";
              return (
              <TableRow key={u.id} className="hover:bg-slate-50/60" data-testid={`row-dev-user-${u.id}`}>
                <TableCell className="font-medium text-slate-900">
                  {u.name || "—"}
                  {isDev && <Badge className="ml-2 bg-slate-800 text-white hover:bg-slate-800 text-[10px]" data-testid={`badge-env-locked-${u.id}`}>via .env</Badge>}
                </TableCell>
                <TableCell className="text-slate-600 break-all">{u.email}</TableCell>
                <TableCell><Badge className={ROLE_TONE[u.role] || ROLE_TONE.agent}>{u.role}</Badge></TableCell>
                <TableCell className="text-slate-700">
                  {u.society_nom ? (
                    <span className="flex items-center gap-2">
                      {u.society_nom}
                      {u.society_status === "closed" && <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100 text-[10px]">Fermée</Badge>}
                    </span>
                  ) : <span className="text-slate-400 italic">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  {isDev ? (
                    <span className="text-xs text-slate-400 italic pr-2" title="Compte DEV géré via .env" data-testid={`row-locked-hint-${u.id}`}>
                      Verrouillé
                    </span>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`btn-edit-user-${u.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmId(u.id)} data-testid={`btn-delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-dev-users" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-dev-user">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-dev-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-dev-user-email" />
            </div>
            <div className="space-y-2">
              <Label>{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe *"}</Label>
              <Input
                type="password"
                required={!editing}
                minLength={editing ? 0 : 6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Laissez vide pour conserver" : ""}
                data-testid="input-dev-user-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="select-dev-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Société {form.role === "dev" && <span className="text-xs text-slate-400">(optionnelle)</span>}</Label>
                <Select value={form.society_id || "__none__"} onValueChange={(v) => setForm({ ...form, society_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="select-dev-user-society"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Aucune —</SelectItem>
                    {societies.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-save-dev-user">
                {editing ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>Action irréversible. L'utilisateur perdra immédiatement l'accès.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-delete-user">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
